import { useState, useEffect, useMemo } from "react";
import {
  Loader2,
  Receipt,
  BookOpen,
  FileCheck,
  ChevronLeft,
  ChevronRight,
  ArrowLeftRight,
  MessageCircle,
  FileText,
  X,
} from "lucide-react";
import { useDossierStore } from "../../store/dossierStore";
import { supabase } from "../../lib/supabase";
import { fetchFactures, fetchAvoirsLiesFactureVente, type FactureWithClient, type FactureStatut } from "../../lib/facturation";
import {
  fetchEcritureFactureVente,
  fetchEcritureFactureVenteImportee,
  fetchEcritureAvoirVente,
  genererEcritureFactureVenteEnvoyee,
  genererEcritureFactureVenteAvoirEnvoyee,
  genererEcritureFactureVenteImportee,
  type EcritureComptable,
} from "../../lib/comptable";
import BlocEcriture, { fmtDate } from "../../components/compta/BlocEcriture";
import EcritureEditModal from "../../components/compta/EcritureEditModal";
import ComptaDrawer from "../../components/compta/ComptaDrawer";
import CommentairesTab from "../../components/compta/CommentairesTab";
import { countCommentaires } from "../../lib/commentaires";
import { formatMontantFCFA } from "@/lib/formatMontant";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { formTabButtonActiveClass, formTabButtonClass, formTabsListClass } from "@/lib/ui/formFieldClasses";
import { resolveFileOrPieceUrl } from "../../lib/piecesJustificativesStorage";

const fmt = (n: number) => formatMontantFCFA(n);

type FactureImportCompta = {
  id: string;
  _source: "import";
  numero: string | null;
  date_emission: string | null;
  date_echeance: string | null;
  client_nom: string | null;
  montant_ht: number;
  montant_tva: number;
  montant_ttc: number;
  statut: "validee" | "comptabilisee";
  comptabilise: boolean;
  ecriture_id: string | null;
  fichier_url: string | null;
};

/** Ligne DB pour le select compta (évite l’union erronée avec Promise.all). */
type FactureVenteImportComptaDbRow = {
  id: string;
  numero: string | null;
  date_emission: string | null;
  date_echeance: string | null;
  client_nom: string | null;
  montant_ht: number | string | null;
  montant_tva: number | string | null;
  montant_ttc: number | string | null;
  statut: string | null;
  comptabilise: boolean | null;
  ecriture_id: string | null;
  fichier_url: string | null;
};

function matchesSearchImport(imp: FactureImportCompta, q: string): boolean {
  if (!q.trim()) return true;
  const s = q.trim().toLowerCase();
  const nom = (imp.client_nom || "").toLowerCase();
  const num = (imp.numero || "").toLowerCase();
  const dateStr = (imp.date_emission || "").slice(0, 10).toLowerCase();
  return nom.includes(s) || num.includes(s) || dateStr.includes(s);
}

type ComptaFilterTab = "toutes" | "boite_reception" | "pre_traitee" | "payee";

const COMPTA_BADGE_A_TRAITER = { bg: "#eff6ff", color: "#2563eb", border: "#dbeafe", label: "À traiter" };
const COMPTA_BADGE_PRE_TRAITEE = { bg: "#fff7ed", color: "#ea580c", border: "#fed7aa", label: "Pré-traitée" };
const COMPTA_BADGE_TRAITEE = { bg: "#ecfdf5", color: "#059669", border: "#d1fae5", label: "Traitée" };

function comptaBadgeForStatut(statut: FactureStatut): { bg: string; color: string; border: string; label: string } {
  if (statut === "brouillon" || statut === "a_envoyer") return COMPTA_BADGE_A_TRAITER;
  if (statut === "envoye" || statut === "a_venir" || statut === "en_retard") return COMPTA_BADGE_PRE_TRAITEE;
  if (statut === "encaissee") return COMPTA_BADGE_TRAITEE;
  return { bg: "#f1f5f9", color: "#475569", border: "#e2e8f0", label: "—" };
}

const FILTER_TABS: { id: ComptaFilterTab; label: string }[] = [
  { id: "toutes", label: "Toutes" },
  { id: "boite_reception", label: "À traiter" },
  { id: "pre_traitee", label: "Pré-traitée" },
  { id: "payee", label: "Traitée" },
];

function matchesSearch(f: FactureWithClient, q: string): boolean {
  if (!q.trim()) return true;
  const s = q.trim().toLowerCase();
  const clientNom = (f.client?.nom || "").toLowerCase();
  return clientNom.includes(s) || (f.numero || "").toLowerCase().includes(s);
}

function matchesStatutFilter(f: FactureWithClient, tab: ComptaFilterTab): boolean {
  if (tab === "toutes") return true;
  if (tab === "boite_reception") return f.statut === "brouillon" || f.statut === "a_envoyer";
  if (tab === "pre_traitee") return f.statut === "envoye" || f.statut === "a_venir" || f.statut === "en_retard";
  if (tab === "payee") return f.statut === "encaissee";
  return true;
}

const PLACEHOLDER_FACTURE = [
  { c: "411", l: "Clients" },
  { c: "4431", l: "TVA collectée" },
  { c: "70", l: "Ventes" },
];

const PLACEHOLDER_AVOIR = [
  { c: "70", l: "Ventes (avoir)" },
  { c: "4431", l: "TVA collectée" },
  { c: "411", l: "Clients" },
];

const PAGE_SIZE = 25;

interface PieceRapprocheePaiement {
  transaction_id: string;
  piece_url: string;
  piece_nom: string | null;
  piece_type: string | null;
  date: string;
  montant_ttc: number;
}

function isPdfPieceType(pieceType: string | null | undefined): boolean {
  return (pieceType || "").toLowerCase().includes("pdf");
}

function isImagePieceType(pieceType: string | null | undefined): boolean {
  return (pieceType || "").toLowerCase().startsWith("image/");
}

export default function FacturesVenteCompta() {
  const { activeDossier } = useDossierStore();
  const [loading, setLoading] = useState(true);
  const [factures, setFactures] = useState<FactureWithClient[]>([]);
  const [importsVente, setImportsVente] = useState<FactureImportCompta[]>([]);
  const [selectedFacture, setSelectedFacture] = useState<FactureWithClient | null>(null);
  const [selectedImport, setSelectedImport] = useState<FactureImportCompta | null>(null);
  const [filterStatut, setFilterStatut] = useState<ComptaFilterTab>("toutes");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [ecritureFacture, setEcritureFacture] = useState<EcritureComptable | null | "loading">(null);
  const [ecritureImporte, setEcritureImporte] = useState<EcritureComptable | null | "loading">(null);
  const [importFichierUrlResolu, setImportFichierUrlResolu] = useState<string | null>(null);
  const [avoirsLies, setAvoirsLies] = useState<Array<{ id: string; numero: string; montant_ttc: number; restant_du_ttc: number; date_emission: string }>>([]);
  const [drawerTab, setDrawerTab] = useState<"ecritures" | "reconciliation" | "commentaires">("ecritures");
  const [piecesRapprochees, setPiecesRapprochees] = useState<PieceRapprocheePaiement[]>([]);
  const [editingEcriture, setEditingEcriture] = useState<EcritureComptable | null>(null);
  const [ecrituresRefreshKey, setEcrituresRefreshKey] = useState(0);
  const [commentairesCount, setCommentairesCount] = useState(0);

  useEffect(() => {
    if (!selectedFacture?.id) {
      setPiecesRapprochees([]);
      return;
    }
    (async () => {
      const { data, error } = await supabase
        .from("facture_paiements")
        .select("transaction_id, transactions(piece_url, piece_nom, piece_type, date, montant_ttc)")
        .eq("facture_id", selectedFacture.id);
      if (error) {
        console.error("[COMPTA] Pièces rapprochement vente:", error);
        setPiecesRapprochees([]);
        return;
      }
      const rows: PieceRapprocheePaiement[] = [];
      const seen = new Set<string>();
      for (const row of data || []) {
        const tid = row.transaction_id as string | null;
        if (!tid) continue;
        const rawT = row.transactions as
          | { piece_url?: string | null; piece_nom?: string | null; piece_type?: string | null; date?: string; montant_ttc?: number }
          | { piece_url?: string | null; piece_nom?: string | null; piece_type?: string | null; date?: string; montant_ttc?: number }[]
          | null;
        const t = Array.isArray(rawT) ? rawT[0] : rawT;
        const url = t?.piece_url?.trim();
        if (!url || seen.has(tid)) continue;
        seen.add(tid);
        rows.push({
          transaction_id: tid,
          piece_url: url,
          piece_nom: t?.piece_nom ?? null,
          piece_type: t?.piece_type ?? null,
          date: t?.date ?? "",
          montant_ttc: Math.abs(Number(t?.montant_ttc) || 0),
        });
      }
      const resolved = await Promise.all(
        rows.map(async (row) => ({
          ...row,
          piece_url: (await resolveFileOrPieceUrl(row.piece_url)) ?? row.piece_url,
        }))
      );
      setPiecesRapprochees(resolved);
    })();
  }, [selectedFacture?.id]);

  useEffect(() => {
    if (!activeDossier?.id) return;
    setLoading(true);
    setSelectedFacture(null);
    setSelectedImport(null);
    (async () => {
      try {
        const [facturesNatives, importsComptabilises] = await Promise.all([
          fetchFactures(activeDossier.id),
          supabase
            .from("factures_vente_import")
            .select(
              "id, numero, date_emission, date_echeance, client_nom, " +
                "montant_ht, montant_tva, montant_ttc, statut, " +
                "comptabilise, ecriture_id, fichier_url"
            )
            .eq("dossier_id", activeDossier.id)
            .eq("comptabilise", true)
            .order("date_emission", { ascending: false }),
        ]);

        setFactures(facturesNatives || []);

        if (importsComptabilises.error) {
          console.error("[COMPTA VTE] Chargement imports comptabilisés:", importsComptabilises.error);
          setImportsVente([]);
        } else {
          const rows = (importsComptabilises.data ?? []) as unknown as FactureVenteImportComptaDbRow[];
          const importRows: FactureImportCompta[] = rows.map((r) => {
            const comptabilise = Boolean(r.comptabilise);
            return {
              id: r.id as string,
              _source: "import",
              numero: (r.numero as string | null) ?? null,
              date_emission: (r.date_emission as string | null) ?? null,
              date_echeance: (r.date_echeance as string | null) ?? null,
              client_nom: (r.client_nom as string | null) ?? null,
              montant_ht: Number(r.montant_ht) || 0,
              montant_tva: Number(r.montant_tva) || 0,
              montant_ttc: Number(r.montant_ttc) || 0,
              statut: "comptabilisee" as const,
              comptabilise,
              ecriture_id: (r.ecriture_id as string | null) ?? null,
              fichier_url: (r.fichier_url as string | null) ?? null,
            };
          });
          setImportsVente(importRows);
        }
      } catch (e) {
        console.error(e);
        setFactures([]);
        setImportsVente([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [activeDossier?.id]);

  useEffect(() => {
    if (!selectedFacture?.id) {
      setEcritureFacture(null);
      setAvoirsLies([]);
      return;
    }
    setEcritureFacture("loading");
    (async () => {
      const isAvoir = !!selectedFacture!.facture_origine_id;
      const fetchDoc = isAvoir ? fetchEcritureAvoirVente : fetchEcritureFactureVente;
      const genererDoc = isAvoir ? genererEcritureFactureVenteAvoirEnvoyee : genererEcritureFactureVenteEnvoyee;

      let fact: EcritureComptable | null = null;
      try {
        fact = await fetchDoc(selectedFacture!.id);
      } catch (err) {
        console.error("[COMPTA] Erreur fetch écritures vente:", err);
      }
      // Fallback: pour les anciennes factures natives créées depuis un import,
      // l'écriture est portée par factures_vente_import (source_type dédié).
      if (!fact && !isAvoir) {
        try {
          const { data: linkedImport } = await supabase
            .from("factures_vente_import")
            .select("id, statut")
            .eq("facture_vente_id", selectedFacture!.id)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();
          const linked = (linkedImport as { id?: string; statut?: string | null } | null) || null;
          if (linked?.id) {
            fact = await fetchEcritureFactureVenteImportee(linked.id);
            if (!fact && linked.statut && ["validee", "valide"].includes(linked.statut)) {
              await genererEcritureFactureVenteImportee(linked.id);
              const refetchedImport = await fetchEcritureFactureVenteImportee(linked.id);
              if (refetchedImport) fact = refetchedImport;
            }
          }
        } catch (err) {
          console.error("[COMPTA] Erreur fallback écriture import vente:", err);
        }
      }
      if (!fact && selectedFacture!.statut && ["envoye", "encaissee"].includes(selectedFacture!.statut)) {
        try {
          await genererDoc(selectedFacture!.id);
          const refetched = await fetchDoc(selectedFacture!.id);
          if (refetched) fact = refetched;
        } catch (err) {
          console.error("[COMPTA] Génération différée facture/avoir vente échouée:", err);
        }
      }
      setEcritureFacture(fact);
      if (!selectedFacture!.facture_origine_id) {
        fetchAvoirsLiesFactureVente(selectedFacture!.id).then(setAvoirsLies).catch(() => setAvoirsLies([]));
      } else {
        setAvoirsLies([]);
      }
    })();
  }, [selectedFacture?.id, ecrituresRefreshKey]);

  useEffect(() => {
    if (!selectedImport?.id) {
      setEcritureImporte(null);
      return;
    }
    setEcritureImporte("loading");
    (async () => {
      try {
        const ec = await fetchEcritureFactureVenteImportee(selectedImport.id);
        setEcritureImporte(ec);
      } catch (err) {
        console.error("[COMPTA VTE] Erreur fetch écriture importée:", err);
        setEcritureImporte(null);
      }
    })();
  }, [selectedImport?.id, ecrituresRefreshKey]);

  useEffect(() => {
    if (!selectedImport?.fichier_url?.trim()) {
      setImportFichierUrlResolu(null);
      return;
    }
    let cancelled = false;
    void resolveFileOrPieceUrl(selectedImport.fichier_url.trim()).then((url) => {
      if (!cancelled) setImportFichierUrlResolu(url ?? selectedImport.fichier_url);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedImport?.id, selectedImport?.fichier_url]);

  const filtered = useMemo(
    () => factures.filter((f) => matchesStatutFilter(f, filterStatut) && matchesSearch(f, search)),
    [factures, filterStatut, search]
  );

  const filteredImports = useMemo(() => {
    if (filterStatut !== "toutes") return [];
    return importsVente.filter((imp) => matchesSearchImport(imp, search));
  }, [importsVente, filterStatut, search]);

  const tabCounts = useMemo(() => {
    const q = search;
    return {
      toutes: factures.filter((f) => matchesSearch(f, q)).length,
      boite_reception: factures.filter((f) => matchesSearch(f, q) && (f.statut === "brouillon" || f.statut === "a_envoyer")).length,
      pre_traitee: factures.filter(
        (f) => matchesSearch(f, q) && (f.statut === "envoye" || f.statut === "a_venir" || f.statut === "en_retard")
      ).length,
      payee: factures.filter((f) => matchesSearch(f, q) && f.statut === "encaissee").length,
    };
  }, [factures, search]);

  const totalCount = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const paginatedFactures = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, safePage]);
  const startIdx = totalCount === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const endIdx = Math.min(safePage * PAGE_SIZE, totalCount);

  useEffect(() => {
    setPage(1);
  }, [filterStatut, search]);

  useEffect(() => {
    if (!selectedFacture?.id) return;
    const fresh = factures.find((x) => x.id === selectedFacture.id);
    if (fresh) setSelectedFacture(fresh);
  }, [factures, selectedFacture?.id]);

  useEffect(() => {
    if (!selectedImport?.id) return;
    const fresh = importsVente.find((x) => x.id === selectedImport.id);
    if (fresh) setSelectedImport(fresh);
  }, [importsVente, selectedImport?.id]);

  useEffect(() => {
    if (!selectedFacture?.id) {
      setCommentairesCount(0);
      return;
    }
    let cancelled = false;
    countCommentaires("facture_vente", selectedFacture.id)
      .then((n) => {
        if (!cancelled) setCommentairesCount(n);
      })
      .catch(() => {
        if (!cancelled) setCommentairesCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedFacture?.id]);

  const statutComptable =
    ecritureFacture === "loading"
      ? "Chargement…"
      : ecritureFacture
        ? ecritureFacture.statut === "validated"
          ? "Validé"
          : "Brouillon"
        : "Non généré";

  const statutComptableImport =
    ecritureImporte === "loading"
      ? "Chargement…"
      : ecritureImporte
        ? ecritureImporte.statut === "validated"
          ? "Validé"
          : "Brouillon"
        : "Non généré";

  const badgeMetier = selectedFacture ? comptaBadgeForStatut(selectedFacture.statut) : null;

  if (!activeDossier) {
    return (
      <div style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif", padding: 24 }}>
        <div style={{ fontWeight: 800, marginBottom: 8, color: "#1A0A00" }}>Sélectionnez un dossier</div>
        <div style={{ fontSize: "0.9rem", color: "#9A8878" }}>Choisissez un dossier pour consulter les écritures comptables.</div>
      </div>
    );
  }

  return (
    <div
      style={{
        fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 64px - 56px)",
        background: "#f8fafc",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "20px 24px", borderBottom: "1px solid #e2e8f0", background: "white" }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: "#0f172a", margin: "0 0 8px 0" }}>Factures clients</h1>
        <p style={{ fontSize: 14, color: "#475569", margin: "0 0 16px 0" }}>Vue comptable — cliquez sur une facture pour afficher les détails</p>

        <div style={{ display: "flex", borderBottom: "1px solid #e5e7eb", marginBottom: 12, alignItems: "center" }}>
          {FILTER_TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setFilterStatut(t.id)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                marginBottom: -1,
                border: "none",
                borderBottom: filterStatut === t.id ? "2px solid #f97316" : "2px solid transparent",
                background: "transparent",
                color: filterStatut === t.id ? "#f97316" : "#6b7280",
                fontSize: 14,
                fontWeight: filterStatut === t.id ? 600 : 500,
                cursor: "pointer",
                transition: "color 0.15s, border-color 0.15s",
              }}
            >
              {t.label}
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "2px 6px",
                  borderRadius: 999,
                  background: filterStatut === t.id ? "#fff7ed" : "#f3f4f6",
                  color: filterStatut === t.id ? "#f97316" : "#6b7280",
                }}
              >
                {tabCounts[t.id]}
              </span>
            </button>
          ))}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <input
            type="text"
            placeholder="Rechercher client, n°…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: 280,
              maxWidth: "100%",
              padding: "8px 14px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              fontSize: 13,
              outline: "none",
              fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
            }}
          />
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto" }}>
        {loading ? (
          <div style={{ padding: 48, textAlign: "center", color: "#475569" }}>
            <Loader2 size={32} className="animate-spin" style={{ animation: "spin 1s linear infinite" }} />
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", background: "white" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #f3f4f6", background: "#fafafa" }}>
                <th style={{ padding: "6px 12px", textAlign: "center", fontSize: 11, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>Statut</th>
                <th style={{ padding: "6px 12px", textAlign: "left", fontSize: 11, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>Date</th>
                <th style={{ padding: "6px 12px", textAlign: "left", fontSize: 11, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>Client</th>
                <th style={{ padding: "6px 12px", textAlign: "left", fontSize: 11, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>Numéro</th>
                <th style={{ padding: "6px 12px", textAlign: "right", fontSize: 11, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>Montant</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && !(filterStatut === "toutes" && filteredImports.length > 0) ? (
                <tr>
                  <td colSpan={5} style={{ padding: 48, textAlign: "center", color: "#475569", fontSize: 14 }}>
                    {factures.length === 0
                      ? "Aucune facture client. Créez des factures depuis Gestion → Facturation."
                      : "Aucune facture ne correspond aux filtres."}
                  </td>
                </tr>
              ) : (
                paginatedFactures.map((f) => {
                  const isSelected = selectedFacture?.id === f.id;
                  const badge = comptaBadgeForStatut(f.statut);
                  const clientLabel = f.facture_origine_id ? `Avoir · ${f.client?.nom || "—"}` : f.client?.nom || "—";
                  return (
                    <tr
                      key={f.id}
                      onClick={() => {
                        setSelectedImport(null);
                        setSelectedFacture(f);
                        setDrawerTab("ecritures");
                      }}
                      style={{
                        cursor: "pointer",
                        background: isSelected ? "linear-gradient(90deg, rgba(255,149,0,0.08), rgba(255,149,0,0.04))" : "transparent",
                        borderBottom: "1px solid #f3f4f6",
                        borderLeft: isSelected ? "4px solid #FF9500" : "4px solid transparent",
                        transition: "background 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.background = "#f9fafb";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = isSelected
                          ? "linear-gradient(90deg, rgba(255,149,0,0.08), rgba(255,149,0,0.04))"
                          : "transparent";
                      }}
                    >
                      <td style={{ padding: "6px 12px", textAlign: "center", verticalAlign: "middle" }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            fontSize: 11,
                            fontWeight: 600,
                            padding: "2px 8px",
                            borderRadius: 999,
                            background: badge.bg,
                            color: badge.color,
                            border: `1px solid ${badge.border}`,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td style={{ padding: "6px 12px", fontSize: 12, color: "#64748b", verticalAlign: "middle" }}>{fmtDate(f.date_emission)}</td>
                      <td style={{ padding: "6px 12px", fontSize: 14, fontWeight: 500, color: "#111827", verticalAlign: "middle" }}>{clientLabel}</td>
                      <td style={{ padding: "6px 12px", fontSize: 12, color: "#9ca3af", verticalAlign: "middle" }}>{f.numero || "—"}</td>
                      <td
                        style={{
                          padding: "6px 12px",
                          textAlign: "right",
                          fontSize: 14,
                          fontWeight: 500,
                          color: "#16a34a",
                          fontVariantNumeric: "tabular-nums",
                          verticalAlign: "middle",
                        }}
                      >
                        +{fmt(Math.abs(Number(f.montant_ttc) || 0))}
                      </td>
                    </tr>
                  );
                })
              )}
              {filterStatut === "toutes" && filteredImports.length > 0 && (
                <>
                  <tr key="__fvi_header__">
                    <td
                      colSpan={5}
                      style={{
                        padding: "10px 12px",
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        color: "#94a3b8",
                        background: "#f8fafc",
                        borderBottom: "1px solid #e2e8f0",
                        borderTop: filtered.length > 0 ? "1px solid #e2e8f0" : undefined,
                      }}
                    >
                      Factures importées — comptabilisées
                    </td>
                  </tr>
                  {filteredImports.map((imp) => {
                    const isSelected = selectedImport?.id === imp.id;
                    return (
                      <tr
                        key={imp.id}
                        onClick={() => {
                          setSelectedFacture(null);
                          setSelectedImport(imp);
                          setDrawerTab("ecritures");
                        }}
                        style={{
                          cursor: "pointer",
                          background: isSelected ? "linear-gradient(90deg, rgba(5,150,105,0.1), rgba(5,150,105,0.04))" : "transparent",
                          borderBottom: "1px solid #f3f4f6",
                          borderLeft: isSelected ? "4px solid #059669" : "4px solid transparent",
                          transition: "background 0.15s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) e.currentTarget.style.background = "#f9fafb";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = isSelected
                            ? "linear-gradient(90deg, rgba(5,150,105,0.1), rgba(5,150,105,0.04))"
                            : "transparent";
                        }}
                      >
                        <td style={{ padding: "6px 12px", textAlign: "center", verticalAlign: "middle" }}>
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              fontSize: 11,
                              fontWeight: 600,
                              padding: "2px 8px",
                              borderRadius: 999,
                              background: "#ecfdf5",
                              color: "#059669",
                              border: "1px solid #bbf7d0",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Comptabilisée
                          </span>
                        </td>
                        <td style={{ padding: "6px 12px", fontSize: 12, color: "#64748b", verticalAlign: "middle" }}>
                          {fmtDate(imp.date_emission ?? "")}
                        </td>
                        <td style={{ padding: "6px 12px", fontSize: 14, fontWeight: 500, color: "#111827", verticalAlign: "middle" }}>
                          {imp.client_nom || "—"}
                        </td>
                        <td style={{ padding: "6px 12px", fontSize: 12, color: "#9ca3af", verticalAlign: "middle" }}>
                          {imp.numero || "—"}
                        </td>
                        <td
                          style={{
                            padding: "6px 12px",
                            textAlign: "right",
                            fontSize: 14,
                            fontWeight: 500,
                            color: "#16a34a",
                            fontVariantNumeric: "tabular-nums",
                            verticalAlign: "middle",
                          }}
                        >
                          +{fmt(Math.abs(imp.montant_ttc))}
                        </td>
                      </tr>
                    );
                  })}
                </>
              )}
            </tbody>
          </table>
        )}
      </div>

      {!loading && totalCount > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
            borderTop: "1px solid #e2e8f0",
            background: "white",
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 13, color: "#64748b" }}>
            {startIdx}–{endIdx} sur {totalCount} facture{totalCount > 1 ? "s" : ""}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                background: safePage <= 1 ? "#f8fafc" : "white",
                color: safePage <= 1 ? "#94a3b8" : "#475569",
                fontSize: 13,
                fontWeight: 600,
                cursor: safePage <= 1 ? "not-allowed" : "pointer",
                fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              }}
            >
              <ChevronLeft size={18} strokeWidth={2} />
              Précédent
            </button>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", minWidth: 80, textAlign: "center" }}>
              Page {safePage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                background: safePage >= totalPages ? "#f8fafc" : "white",
                color: safePage >= totalPages ? "#94a3b8" : "#475569",
                fontSize: 13,
                fontWeight: 600,
                cursor: safePage >= totalPages ? "not-allowed" : "pointer",
                fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              }}
            >
              Suivant
              <ChevronRight size={18} strokeWidth={2} />
            </button>
          </div>
        </div>
      )}

      {(selectedFacture || selectedImport) && (
        <ComptaDrawer
          open={!!selectedFacture || !!selectedImport}
          onClose={() => {
            setSelectedFacture(null);
            setSelectedImport(null);
          }}
          width={560}
        >
          {selectedFacture ? (
          <div className="flex flex-1 flex-col overflow-hidden">
            <div className="flex-shrink-0 border-b border-[#e4e8ef] bg-white px-5 py-4">
              <div className="mb-2 flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="!p-1.5"
                  onClick={() => {
                    setSelectedFacture(null);
                    setSelectedImport(null);
                  }}
                  aria-label="Fermer"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="mb-1 text-2xl font-bold tabular-nums text-green-600">
                + {fmt(Math.abs(Number(selectedFacture.montant_ttc) || 0))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-[15px] font-semibold text-slate-900">
                  {selectedFacture.facture_origine_id ? "Avoir · " : ""}
                  {selectedFacture.client?.nom || "—"}
                </div>
                <div className="text-[12px] text-slate-400">{fmtDate(selectedFacture.date_emission)}</div>
                {badgeMetier && (
                  <span
                    className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                    style={{ background: badgeMetier.bg, color: badgeMetier.color, border: `1px solid ${badgeMetier.border}` }}
                  >
                    {badgeMetier.label}
                  </span>
                )}
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                  style={{
                    background: statutComptable === "Validé" ? "#f0fdf4" : statutComptable === "Non généré" ? "#f8fafc" : "#fff7ed",
                    color: statutComptable === "Validé" ? "#15803d" : statutComptable === "Non généré" ? "#64748b" : "#c2410c",
                    border:
                      statutComptable === "Validé"
                        ? "1px solid #bbf7d0"
                        : statutComptable === "Non généré"
                          ? "1px solid #e2e8f0"
                          : "1px solid #fed7aa",
                  }}
                >
                  <FileCheck size={12} strokeWidth={2} className="shrink-0" />
                  {statutComptable}
                </span>
              </div>
            </div>

            <div className={cn(formTabsListClass, "!mb-0 flex-shrink-0 bg-white px-2")}>
              {(
                [
                  { id: "ecritures" as const, label: "Écritures", icon: BookOpen },
                  { id: "reconciliation" as const, label: "Réconciliation", icon: ArrowLeftRight },
                  { id: "commentaires" as const, label: "Commentaires", icon: MessageCircle },
                ] as const
              ).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setDrawerTab(t.id)}
                  className={cn(
                    formTabButtonClass,
                    drawerTab === t.id && formTabButtonActiveClass,
                    "inline-flex items-center gap-1.5"
                  )}
                >
                  <t.icon size={14} className="shrink-0" />
                  {t.label}
                  {t.id === "commentaires" && commentairesCount > 0 && (
                    <span className="ml-1 inline-flex min-h-4 min-w-[15px] items-center justify-center rounded-full bg-amber-50 px-1.5 py-px text-[10px] font-semibold text-amber-700">
                      {commentairesCount}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="flex flex-1 flex-col gap-4 overflow-y-auto bg-white px-5 py-4">
              {drawerTab === "ecritures" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                  <section>
                    <h3
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: 800,
                        color: "#0f172a",
                        marginBottom: 10,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <Receipt size={18} strokeWidth={2} color="#F97316" />
                      {selectedFacture.facture_origine_id ? "Avoir" : "Facture"}
                    </h3>
                    {ecritureFacture === "loading" ? (
                      <div style={{ padding: 32, textAlign: "center", color: "#64748b" }}>
                        <Loader2 size={24} style={{ animation: "spin 1s linear infinite" }} />
                      </div>
                    ) : (
                      <BlocEcriture
                        titre={
                          selectedFacture.facture_origine_id
                            ? "Écriture d'avoir (D70/D443/C411)"
                            : "Écriture de vente (D411/C443/C70)"
                        }
                        ecriture={ecritureFacture}
                        emptyMessage="Aucune écriture comptable générée"
                        emptySubtext={
                          selectedFacture.facture_origine_id
                            ? "L'écriture sera créée lors de l'envoi de l'avoir au client."
                            : "L'écriture sera créée lors de l'envoi de la facture au client."
                        }
                        placeholderRows={selectedFacture.facture_origine_id ? PLACEHOLDER_AVOIR : PLACEHOLDER_FACTURE}
                        onEditDraft={
                          ecritureFacture?.statut === "draft" ? () => setEditingEcriture(ecritureFacture) : undefined
                        }
                      />
                    )}
                  </section>

                  {selectedFacture.facture_origine_id ? (
                    <section>
                      <h3
                        style={{
                          fontSize: "0.85rem",
                          fontWeight: 800,
                          color: "#0f172a",
                          marginBottom: 10,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <FileText size={18} strokeWidth={2} color="#9A8878" />
                        Facture d&apos;origine
                      </h3>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          const orig = factures.find((f) => f.id === selectedFacture.facture_origine_id);
                          if (orig) setSelectedFacture(orig);
                        }}
                        onKeyDown={(e) => {
                          if (e.key !== "Enter") return;
                          const orig = factures.find((f) => f.id === selectedFacture.facture_origine_id);
                          if (orig) setSelectedFacture(orig);
                        }}
                        style={{
                          padding: "12px 14px",
                          background: "#FAFAF9",
                          border: "1px solid #EAE0D5",
                          borderRadius: "10px",
                          cursor: factures.some((f) => f.id === selectedFacture.facture_origine_id) ? "pointer" : "default",
                          fontSize: "0.9rem",
                          color: "#1A0A00",
                        }}
                      >
                        {selectedFacture.facture_origine_numero || selectedFacture.facture_origine_id || "—"}
                        {factures.some((f) => f.id === selectedFacture.facture_origine_id) && (
                          <span style={{ marginLeft: 8, fontSize: "0.8rem", color: "#9A8878" }}>→ Cliquer pour afficher</span>
                        )}
                      </div>
                    </section>
                  ) : avoirsLies.length > 0 ? (
                    <section>
                      <h3
                        style={{
                          fontSize: "0.85rem",
                          fontWeight: 800,
                          color: "#0f172a",
                          marginBottom: 10,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <FileText size={18} strokeWidth={2} color="#9A8878" />
                        Avoirs liés
                      </h3>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {avoirsLies.map((a) => (
                          <div
                            key={a.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              const fv = factures.find((x) => x.id === a.id);
                              if (fv) setSelectedFacture(fv);
                            }}
                            onKeyDown={(e) => {
                              if (e.key !== "Enter") return;
                              const fv = factures.find((x) => x.id === a.id);
                              if (fv) setSelectedFacture(fv);
                            }}
                            style={{
                              padding: "10px 12px",
                              background: "#FEFCE8",
                              border: "1px solid #FEF08A",
                              borderRadius: "8px",
                              cursor: factures.some((x) => x.id === a.id) ? "pointer" : "default",
                              fontSize: "0.85rem",
                              color: "#1A0A00",
                            }}
                          >
                            Avoir {a.numero} · {fmt(a.montant_ttc)} · {fmtDate(a.date_emission)}
                            {factures.some((x) => x.id === a.id) && (
                              <span style={{ marginLeft: 6, fontSize: "0.75rem", color: "#9A8878" }}>→ Voir</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </section>
                  ) : null}

                  <section>
                    <h3
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: 800,
                        color: "#0f172a",
                        marginBottom: 10,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <BookOpen size={18} strokeWidth={2} color="#6366f1" />
                      Résumé comptable
                    </h3>
                    <div
                      style={{
                        background: "#FAFAFA",
                        borderRadius: "10px",
                        padding: "14px 16px",
                        border: "1px solid #E2E8F0",
                      }}
                    >
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "6px 12px",
                            borderRadius: "8px",
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            background:
                              statutComptable === "Validé"
                                ? "#ECFDF5"
                                : statutComptable === "Non généré"
                                  ? "#F1F5F9"
                                  : "#FFF7ED",
                            color:
                              statutComptable === "Validé"
                                ? "#059669"
                                : statutComptable === "Non généré"
                                  ? "#64748b"
                                  : "#C2410C",
                            border:
                              statutComptable === "Validé"
                                ? "1px solid #A7F3D0"
                                : statutComptable === "Non généré"
                                  ? "1px solid #E2E8F0"
                                  : "1px solid #FED7AA",
                          }}
                        >
                          <FileCheck size={14} strokeWidth={2.5} />
                          {statutComptable}
                        </span>
                        <span
                          style={{
                            padding: "6px 12px",
                            borderRadius: "8px",
                            fontSize: "0.78rem",
                            fontWeight: 600,
                            background: selectedFacture.facture_origine_id ? "#FEFCE8" : "#EEF2FF",
                            color: selectedFacture.facture_origine_id ? "#A16207" : "#4338ca",
                            border: selectedFacture.facture_origine_id ? "1px solid #FEF08A" : "1px solid #C7D2FE",
                          }}
                        >
                          {selectedFacture.facture_origine_id ? "Avoir de vente" : "Facture de vente"}
                        </span>
                        {ecritureFacture && ecritureFacture !== "loading" && (
                          <span
                            style={{
                              padding: "6px 12px",
                              borderRadius: "8px",
                              fontSize: "0.78rem",
                              fontWeight: 600,
                              fontFamily: "'JetBrains Mono', 'Space Mono', monospace",
                              background: "#FFF7ED",
                              color: "#9a3412",
                              border: "1px solid #FED7AA",
                            }}
                          >
                            {ecritureFacture.journal_code} · Ventes
                          </span>
                        )}
                        {selectedFacture.client?.regime_imposition && (
                          <span
                            style={{
                              padding: "6px 12px",
                              borderRadius: "8px",
                              fontSize: "0.78rem",
                              fontWeight: 600,
                              background: "#E0F2FE",
                              color: "#0369a1",
                              border: "1px solid #BAE6FD",
                            }}
                          >
                            {selectedFacture.client.regime_imposition}
                          </span>
                        )}
                      </div>
                    </div>
                  </section>
                </div>
              )}

              {drawerTab === "reconciliation" && (
                <div style={{ padding: 8, fontSize: 14, color: "#475569", lineHeight: 1.6 }}>
                  <p style={{ margin: "0 0 12px 0" }}>
                    Rapprochez les encaissements bancaires avec cette facture depuis <strong>Gestion → Facturation</strong> (factures
                    clients).
                  </p>
                  <p style={{ margin: "0 0 8px 0", color: "#64748b", fontSize: 13 }}>
                    Reste à encaisser (TTC) : <strong>{fmt(Math.max(0, Number(selectedFacture.restant_du_ttc) || 0))}</strong>
                  </p>
                  <p style={{ margin: "0 0 16px 0", color: "#64748b", fontSize: 13 }}>
                    Statut métier : <strong>{selectedFacture.statut === "encaissee" ? "Encaissée" : "Non soldée"}</strong>
                  </p>
                  <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 16 }}>
                    <h4 style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 12px 0" }}>
                      Pièces justificatives des paiements
                    </h4>
                    {piecesRapprochees.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        {piecesRapprochees.map((p) => (
                          <div key={p.transaction_id} style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, background: "#fafafa" }}>
                            {isPdfPieceType(p.piece_type) ? (
                              <a
                                href={p.piece_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                  textDecoration: "none",
                                  color: "#0f172a",
                                }}
                              >
                                <FileText size={28} strokeWidth={1.75} color="#64748b" style={{ flexShrink: 0 }} />
                                <span style={{ fontSize: 13, fontWeight: 600, wordBreak: "break-word" }}>{p.piece_nom || "Document PDF"}</span>
                              </a>
                            ) : isImagePieceType(p.piece_type) ? (
                              <a href={p.piece_url} target="_blank" rel="noopener noreferrer" style={{ display: "block", lineHeight: 0 }}>
                                <img
                                  src={p.piece_url}
                                  alt={p.piece_nom || "Pièce"}
                                  style={{ maxHeight: 100, maxWidth: "100%", borderRadius: 8, objectFit: "contain", cursor: "pointer" }}
                                />
                              </a>
                            ) : (
                              <a
                                href={p.piece_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: "#0f172a" }}
                              >
                                <FileText size={22} color="#64748b" />
                                {p.piece_nom || "Ouvrir la pièce"}
                              </a>
                            )}
                            <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
                              {fmtDate(p.date)} · {fmt(p.montant_ttc)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ margin: 0, fontSize: 13, color: "#94a3b8", fontStyle: "italic" }}>Aucune pièce jointe sur les paiements</p>
                    )}
                  </div>
                </div>
              )}

              {drawerTab === "commentaires" && (
                <div style={{ minHeight: 420, maxHeight: "70vh", display: "flex", flexDirection: "column" }}>
                  <CommentairesTab
                    dossierId={activeDossier.id}
                    entiteType="facture_vente"
                    entiteId={selectedFacture.id}
                    onCommentsChanged={() => {
                      void countCommentaires("facture_vente", selectedFacture.id).then(setCommentairesCount);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          ) : selectedImport ? (
          <div className="flex flex-1 flex-col overflow-hidden">
            <div className="flex-shrink-0 border-b border-[#e4e8ef] bg-white px-5 py-4">
              <div className="mb-2 flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="!p-1.5"
                  onClick={() => {
                    setSelectedFacture(null);
                    setSelectedImport(null);
                  }}
                  aria-label="Fermer"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-green-700">Facture importée — comptabilisée</div>
              <div className="mb-1 text-2xl font-bold tabular-nums text-green-600">+ {fmt(Math.abs(selectedImport.montant_ttc))}</div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-[15px] font-semibold text-slate-900">{selectedImport.client_nom || "—"}</div>
                <div className="text-[12px] text-slate-400">
                  {fmtDate(selectedImport.date_emission ?? "")}
                  {selectedImport.numero ? ` · N° ${selectedImport.numero}` : ""}
                </div>
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                  style={{
                    background:
                      statutComptableImport === "Validé"
                        ? "#f0fdf4"
                        : statutComptableImport === "Non généré"
                          ? "#f8fafc"
                          : "#fff7ed",
                    color:
                      statutComptableImport === "Validé"
                        ? "#15803d"
                        : statutComptableImport === "Non généré"
                          ? "#64748b"
                          : "#c2410c",
                    border:
                      statutComptableImport === "Validé"
                        ? "1px solid #bbf7d0"
                        : statutComptableImport === "Non généré"
                          ? "1px solid #e2e8f0"
                          : "1px solid #fed7aa",
                  }}
                >
                  <FileCheck size={12} strokeWidth={2} className="shrink-0" />
                  {statutComptableImport}
                </span>
              </div>
            </div>
            <div className="flex flex-1 flex-col gap-4 overflow-y-auto bg-white px-5 py-4">
              {importFichierUrlResolu && (
                <section style={{ marginBottom: 24 }}>
                  <h3
                    style={{
                      fontSize: "0.85rem",
                      fontWeight: 800,
                      color: "#0f172a",
                      marginBottom: 10,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <FileText size={18} strokeWidth={2} color="#F97316" />
                    Pièce importée
                  </h3>
                  <a
                    href={importFichierUrlResolu}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: 13, fontWeight: 600, color: "#2563eb", display: "inline-block", marginBottom: 12 }}
                  >
                    Ouvrir le fichier dans un nouvel onglet
                  </a>
                  {(importFichierUrlResolu.toLowerCase().includes(".pdf") ||
                    importFichierUrlResolu.toLowerCase().includes("pdf")) && (
                    <iframe
                      title="Facture importée"
                      src={importFichierUrlResolu}
                      style={{
                        width: "100%",
                        height: 420,
                        border: "1px solid #e2e8f0",
                        borderRadius: 10,
                        background: "#fafafa",
                      }}
                    />
                  )}
                </section>
              )}
              <section style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: "0.85rem", fontWeight: 800, color: "#0f172a", marginBottom: 10 }}>Montants</h3>
                <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.8 }}>
                  <div>
                    <strong>HT</strong> : {fmt(selectedImport.montant_ht)}
                  </div>
                  <div>
                    <strong>TVA</strong> : {fmt(selectedImport.montant_tva)}
                  </div>
                  <div>
                    <strong>TTC</strong> : {fmt(selectedImport.montant_ttc)}
                  </div>
                </div>
              </section>
              <section>
                <h3
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: 800,
                    color: "#0f172a",
                    marginBottom: 10,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <Receipt size={18} strokeWidth={2} color="#F97316" />
                  Écriture comptable
                </h3>
                {ecritureImporte === "loading" ? (
                  <div style={{ padding: 32, textAlign: "center", color: "#64748b" }}>
                    <Loader2 size={24} style={{ animation: "spin 1s linear infinite" }} />
                  </div>
                ) : (
                  <BlocEcriture
                    titre="Écriture de vente importée (D411/C443/C70)"
                    ecriture={ecritureImporte}
                    emptyMessage="Aucune écriture comptable liée à cet import"
                    emptySubtext="L'écriture est créée lors de la comptabilisation depuis l'import facture vente."
                    placeholderRows={PLACEHOLDER_FACTURE}
                    onEditDraft={
                      ecritureImporte && ecritureImporte.statut === "draft"
                        ? () => setEditingEcriture(ecritureImporte)
                        : undefined
                    }
                  />
                )}
              </section>
            </div>
          </div>
          ) : null}
        </ComptaDrawer>
      )}

      {editingEcriture && (
        <EcritureEditModal
          ecriture={editingEcriture}
          onClose={() => setEditingEcriture(null)}
          onSaved={() => setEcrituresRefreshKey((k) => k + 1)}
          onValidated={() => setEcrituresRefreshKey((k) => k + 1)}
        />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
